<template>
  <div class="page-table scrollable only-y">
    <div class="no-table-box page-header">
      <el-row class="hidden-sm-and-up pb-15">
        <h1>
          <v-svg class="voxo-yellow" sprite="devices" />
          Devices
        </h1>
      </el-row>
      <el-row class="hidden-xs-only pb-15">
        <el-col :span="12">
          <h1>
            <v-svg class="voxo-yellow" sprite="devices" />
            Devices
          </h1>
        </el-col>
        <el-col class="text-right" :span="12">
          <el-tag class="mr-5">
            <i class="el-icon-info" style="margin-right: 4px" />
            Total: {{ total | number }}
          </el-tag>
          <el-tag class="mr-5">
            <status-indicator :pulse="false" status="positive" />
            Online: {{ onlineCount }}
          </el-tag>
          <el-tag>
            <status-indicator :pulse="false" status="negative" />
            Offline: {{ offlineCount }}
          </el-tag>
        </el-col>
      </el-row>
      <div class="flex pb-20">
        <div class="box">
          <el-input v-model="search" placeholder="Search" clearable />
          <el-button class="ml-10 hidden-xs-only" @click="doExport">
            Export
          </el-button>
        </div>
        <div class="box right">
          <el-dropdown class="mr-10">
            <el-button>
              Bulk
              <i class="el-icon-arrow-down el-icon--right"></i>
            </el-button>

            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item @click.native="openBulkUpdateModal">
                Settings
              </el-dropdown-item>

              <el-dropdown-item @click.native="openBulkRebootModal">
                Reboot
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>

          <el-dropdown>
            <el-button>
              Add Device
              <i class="el-icon-arrow-down el-icon--right"></i>
            </el-button>

            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item @click.native="openCreateModal">
                Add New
              </el-dropdown-item>

              <el-dropdown-item @click.native="bulkCreateVisible = true">
                Bulk Add
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </div>
      <div class="vxe-table-height card-base card-shadow--medium">
        <vxe-grid
          round
          border="inner"
          ref="xGrid"
          height="100%"
          auto-resize
          empty-text="No Data"
          show-overflow="tooltip"
          highlight-hover-row
          @cell-click="cellClicked"
          @cell-mouseenter="rowHover"
          @cell-mouseleave="rowLeave"
          :data="tableData"
          :scroll-y="{ enabled: true, gt: 50, oSize: 0 }"
          :loading="tableLoading"
          :columns="tableColumns"
        />
      </div>
    </div>

    <create-form
      :createVisible="createVisible"
      :macInUse="macInUse"
      :deviceModelOptions="deviceModelOptions"
      :phonebooksOptions="phonebooksOptions"
      :buttonLayoutOptions="buttonLayoutOptions"
      :createProcessing="createProcessing"
      :clearFormFields="clearFormFields"
      @checkMac="checkMac"
      @createOperation="createOperation"
      @cancelOperation="cancelOperation"
    />

    <bulk-create
      :visible="bulkCreateVisible"
      @modalClosed="bulkCreateVisible = false"
    />

    <bulk-update
      :visible="bulkUpdateVisible"
      :processing="bulkUpdateProcessing"
      :clearFormFields="clearFormFields"
      :deviceModelOptions="deviceModelOptions"
      @updateOperation="bulkUpdateOperation"
      @cancelOperation="cancelOperation"
    />

    <bulk-reboot
      :bulkRebootVisible="bulkRebootVisible"
      :bulkRebootProcessing="bulkRebootProcessing"
      :deviceModelOptions="deviceModelOptions"
      :clearFormFields="clearFormFields"
      @bulkRebootOperation="bulkRebootOperation"
      @cancelOperation="cancelOperation"
    />

    <edit-form
      :editVisible="editVisible"
      :selectedDevice="selectedDevice"
      :deviceModelOptions="deviceModelOptions"
      :phonebooksOptions="phonebooksOptions"
      :buttonLayoutOptions="buttonLayoutOptions"
      :editProcessing="editProcessing"
      :clearFormFields="clearFormFields"
      @checkMac="checkMac"
      @editOperation="editOperation"
      @cancelOperation="cancelOperation"
    />

    <el-dialog
      class="duplicate-modal form-modal"
      :show-close="false"
      :fullscreen="false"
      :modal="true"
      :visible.sync="duplicateVisible"
      width="35%"
      center
      :before-close="cancelDuplicate"
    >
      <div v-loading="duplicateProcessing">
        <div class="duplicate-modal__header" slot="title">
          <h2 class="duplicate-modal__title">Duplicate a device</h2>
        </div>
        <el-form>
          <el-input v-model="duplicateDeviceForm.name" placeholder="Enter name">
            <template slot="prefix">Name</template>
          </el-input>
          <div class="el-input el-input--prefix">
            <input
              type="text"
              autocomplete="off"
              placeholder="Enter MAC address"
              class="el-input__inner"
              :value="duplicateDeviceForm.mac"
              @input="
                duplicateDeviceForm.mac = $event.target.value.toUpperCase()
              "
              v-mask="'NN:NN:NN:NN:NN:NN'"
            />
            <span class="el-input__prefix">MAC address</span>
            <div v-if="macUsed" class="el-input-group__append">
              <in-use :inUse="macInUse" />
            </div>
          </div>
        </el-form>
        <span slot="footer">
          <el-button
            type="primary"
            @click="duplicateDevice"
            :disabled="macUsed"
          >
            Duplicate
          </el-button>
          <el-button
            type="default"
            class="hidden-xs-only"
            @click="cancelDuplicate"
          >
            Cancel
          </el-button>
          <el-button
            type="default"
            icon="el-icon-close"
            class="hidden-sm-and-up"
            @click="cancelDuplicate"
          />
        </span>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { debounce } from "lodash";
import { mapActions } from "vuex";
import { ExportToCsv } from "export-to-csv";

import { globalEventBus } from "../../../globalEventBus";
import feathersClient from "../../../feathers/feathers-client";
import CreateForm from "./components/createForm";
import BulkCreate from "./components/bulk-create";
import BulkUpdate from "./components/bulkUpdate";
import BulkReboot from "./components/bulkReboot";
import EditForm from "./components/editForm";
import InUse from "@/components/InUse";

export default {
  name: "DevicesTable",

  components: {
    CreateForm,
    BulkCreate,
    EditForm,
    InUse,
    BulkUpdate,
    BulkReboot,
  },

  data() {
    return {
      search: "",
      total: 0,
      devicesChannel: null,
      tableData: [],
      allDevices: [],
      visibleActionsId: "",
      clearFormFields: false,
      editVisible: false,
      editProcessing: false,
      createVisible: false,
      bulkCreateVisible: false,
      createProcessing: false,
      mac: null,
      macInUse: null,
      deviceModelOptions: [],
      phonebooksOptions: [],
      selectedDevice: null,
      tableLoading: false,
      buttonLayoutOptions: [],
      duplicateVisible: false,
      shouldReboot: false,
      deviceToDuplicate: {
        name: "",
        id: null,
      },
      duplicateDeviceForm: {
        name: "",
        mac: "",
      },
      duplicateProcessing: false,
      tableColumns: [
        {
          field: "sprite",
          title: "",
          width: 50,
          slots: {
            default: ({ row }) => {
              return [
                <div class="voxo-square-icon voxo-bg-btn">
                  <v-svg class="voxo-yellow" sprite="devices" />
                </div>,
              ];
            },
          },
        },
        {
          field: "status",
          title: "",
          width: 45,
          slots: {
            default: ({ row }) => {
              return [
                <status-indicator
                  pulse={false}
                  status={row.userAgent ? "positive" : "negative"}
                />,
              ];
            },
          },
        },
        {
          field: "name",
          title: "Name",
          sortable: true,
          minWidth: 150,
          showOverflow: true,
        },
        {
          field: "deviceModel",
          title: "Model",
          sortable: true,
          minWidth: 100,
          showOverflow: false,
        },
        {
          field: "mac",
          title: "MAC Address",
          sortable: true,
          minWidth: 150,
          showOverflow: false,
        },
        //adding userAgent field
        {
          field: "userAgent",
          title: "User Agent",
          sortable: true,
          minWidth: 300,
          showOverflow: true,
        },
        {
          field: "actions",
          title: "",
          minWidth: "200",
          showOverflow: false,
          slots: {
            default: ({ row }) => {
              return [
                <div v-show={row.id === this.visibleActionsId}>
                  <el-tooltip placement="top" content="Download Local Config">
                    <el-button
                      id={"local-config-" + row.id}
                      v-show={this.isYealink(row.mac)}
                      icon="el-icon-download"
                      onClick={() => this.downloadLocalConfig(row.id)}
                      class="table-action-button icon-blue"
                    />
                  </el-tooltip>
                  <el-tooltip placement="top" content="Duplicate">
                    <el-button
                      id={"duplicate-" + row.id}
                      icon="el-icon-document"
                      onClick={() => this.openDuplicateModal(row.id, row.name)}
                      class="table-action-button icon-blue"
                    />
                  </el-tooltip>
                  <el-tooltip placement="top" content="Edit">
                    <el-button
                      id={"edit-" + row.id}
                      icon="el-icon-edit"
                      onClick={() => this.openEditModal(row.id)}
                      class="table-action-button icon-blue"
                    />
                  </el-tooltip>
                  <el-tooltip placement="top" content="Delete Local Config">
                    <el-button
                      id={"edit-" + row.id}
                      v-show={this.isYealink(row.mac)}
                      icon="el-icon-error"
                      onClick={() => this.deleteLocalConfig(row)}
                      class="table-action-button icon-orange"
                    />
                  </el-tooltip>
                  <el-tooltip placement="top" content="Factory Reset">
                    <el-button
                      id={"edit-" + row.id}
                      v-show={this.isYealink(row.mac)}
                      icon="el-icon-refresh"
                      onClick={() => this.factoryResetDevice(row)}
                      class="table-action-button icon-orange"
                    />
                  </el-tooltip>
                  <el-tooltip placement="top" content="Delete">
                    <el-button
                      id={"delete-" + row.id}
                      icon="el-icon-delete"
                      onClick={() => this.deleteOperation(row.id)}
                      class="table-action-button icon-red"
                    />
                  </el-tooltip>
                </div>,
              ];
            },
          },
        },
      ],

      offlineCount: 0,
      onlineCount: 0,

      // ⭐️ Bulk Reset ⭐️ //
      bulkRebootVisible: false,
      bulkRebootProcessing: false,

      // ⭐️ Bulk Update ⭐️ //
      bulkUpdateVisible: false,
      bulkUpdateProcessing: false,
    };
  },

  computed: {
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },
    user() {
      return this.$store.state.auth.user;
    },
    duplicateDeviceMac() {
      return this.duplicateDeviceForm.mac;
    },
    macUsed() {
      return this.macInUse && this.macInUse.inUse;
    },
  },

  watch: {
    selectedTenant() {
      this.findDevices();
      this.getPhonebookOptions();
      this.getButtonLayoutOptions();
      feathersClient
        .service("channel-memberships")
        .remove(null, { query: { channelName: this.devicesChannel } });
      this.joinSocketChannel();
    },

    duplicateDeviceMac(val) {
      if (val) {
        this.mac = val;
      }
    },

    user() {
      this.findDevices();
      this.getdeviceModelOptions();
      if (!this.devicesChannel) {
        this.joinSocketChannel();
      }
    },

    mac: debounce(function () {
      if (this.mac) {
        feathersClient
          .service("check-number")
          .find({
            query: { mac: this.mac },
          })
          .then((res) => {
            this.macInUse = res;
          });
      }
    }, 500),

    search: debounce(function () {
      this.searchDevices();
    }, 500),

    tableData(data) {
      this.onlineCount = 0;
      this.offlineCount = 0;

      if (data) {
        for (let row of data) {
          if (row.userAgent) {
            this.onlineCount++;
            continue;
          }

          this.offlineCount++;
        }
      }
    },
  },

  methods: {
    ...mapActions("devices", {
      createDevice: "create",
      deleteDevice: "remove",
      editDevice: "patch",
    }),

    ...mapActions("button-layouts", { findButtonLayouts: "find" }),

    doExport() {
      const data = [...this.tableData];
      if (data.length) {
        const writeData = data.map((item) => ({
          name: item.name,
          model: item.deviceModel,
          mac: item.mac,
        }));

        const options = {
          filename: "devices",
          showLabels: true,
          headers: ["Name", "Model", "MAC Address", "User Agent"],
        };

        const csvExporter = new ExportToCsv(options);
        csvExporter.generateCsv(writeData);
      }
    },

    openDuplicateModal(id, name) {
      this.deviceToDuplicate.id = id;
      this.deviceToDuplicate.name = name;
      this.duplicateVisible = true;
    },

    duplicateDevice() {
      feathersClient
        .service("devices")
        .get(this.deviceToDuplicate.id)
        .then((deviceToDuplicate) => {
          const {
            phPmId,
            phonebooks,
            settings,
            buttonlayouts,
            sidecarButtonLayouts,
          } = deviceToDuplicate;
          const { name, mac } = this.duplicateDeviceForm;
          const createBody = {
            tenantId: this.selectedTenant.id,
            name,
            mac,
            phPmId,
            phoneLines: [],
            phonebooks: [],
            settings: [],
            buttonlayouts,
            sidecarButtonLayouts,
          };
          createBody.phonebooks = phonebooks.map(
            ({ phonebookId }) => phonebookId
          );
          createBody.settings = settings.map(
            ({ tenantId, type, alId, order, avValue }) => ({
              tenantId,
              type,
              alId,
              order: `${order}`,
              avValue,
            })
          );
          this.duplicateProcessing = true;
          this.createDevice(createBody)
            .then(() => {
              this.$notify({
                title: "Success",
                message: "Duplicated successfully!",
                type: "success",
              });
              this.duplicateProcessing = false;
              this.cancelDuplicate();
            })
            .catch((error) => {
              if (error.hasOwnProperty("errors")) {
                this.$notify({
                  title: error.message,
                  message: error.errors[Object.keys(error.errors)[0]],
                  type: "error",
                });
              } else {
                this.$notify({
                  title: error.name,
                  message: error.message,
                  type: "error",
                });
              }
              this.duplicateProcessing = false;
            });
        });
    },

    cancelDuplicate() {
      this.duplicateDeviceForm = {
        name: "",
        mac: "",
      };
      this.deviceToDuplicate = {
        id: null,
        name: "",
      };
      this.duplicateVisible = false;
      this.mac = null;
      this.macInUse = null;
    },

    getButtonLayoutOptions() {
      this.findButtonLayouts({
        query: {
          tenantId: this.selectedTenant.id,
        },
      }).then((res) => {
        this.buttonLayoutOptions = [...res];
      }).catch((e) => {
        this.buttonLayoutOptions = [];
      });
    },

    downloadLocalConfig(deviceId) {
      this.tableLoading = true;
      feathersClient
        .service("devices")
        .get(deviceId)
        .then((res) => {
          if (res.file) {
            const link = document.createElement("a");
            link.href = res.file.raw;
            link.download = res.file.name;
            link.click();
          } else {
            this.$notify({
              title: "Not Found",
              message: "Local Config Not Found.",
              type: "warning",
            });
          }
          this.tableLoading = false;
        })
        .catch((e) => {
          console.log(e);
          this.tableLoading = false;
        });
    },

    deleteLocalConfig(device) {
      const { id, mac } = device;

      feathersClient
        .service("devices")
        .update(id, {
          mac,
          updateType: "deleteLocalConfig",
        })
        .then(() => {
          this.$notify({
            message: "Local config successfully removed",
            title: "Success",
            type: "success",
          });
        })
        .catch(() => {
          this.$notify({
            message: "Failed to delete local config",
            title: "Error",
            type: "error",
          });
        });
    },

    factoryResetDevice(device) {
      const { id, mac } = device;
      feathersClient
        .service("devices")
        .update(id, {
          mac,
          updateType: "factoryReset",
        })
        .then(() => {
          this.$notify({
            message: "Factory reset successfully sent",
            title: "Success",
            type: "success",
          });
        })
        .catch(() => {
          this.$notify({
            message: "Failed to send factory reset",
            title: "Error",
            type: "error",
          });
        });
    },

    //General Form and Table Operations
    cellClicked(data) {
      if (data.column.property !== "actions") {
        this.openEditModal(data.row.id);
      }
    },

    rowHover(data) {
      this.visibleActionsId = data.row.id;
    },

    rowLeave() {
      this.visibleActionsId = "";
    },

    findDevices() {
      this.tableLoading = true;
      this.tableData = [];
      feathersClient
        .service("devices")
        .find({
          query: {
            tenantId: this.selectedTenant.id,
          },
        })
        .then((res) => {
          this.tableData = [...res];
          this.allDevices = [...res];
          this.total = res.length;
          this.tableLoading = false;
        })
        .catch(() => {
          this.tableLoading = false;
        });
    },

    searchDevices() {
      if (this.search.length) {
        this.tableData = this.allDevices.filter(
          (item) =>
            item.name.toLowerCase().includes(this.search.toLowerCase()) ||
            item.mac.toLowerCase().includes(this.search.toLowerCase())
        );
      } else {
        this.tableData = [...this.allDevices];
      }
      this.total = this.tableData.length;
    },

    checkMac(mac) {
      this.mac = mac;
    },

    createOperation(createBody) {
      this.$confirm("Process device create?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning",
      })
        .then(() => {
          this.createProcessing = true;

          this.createDevice(createBody)
            .then(() => {
              this.$notify({
                title: "Success",
                message: "Created successfully!",
                type: "success",
              });
              this.createProcessing = false;
              this.cancelOperation("create");
            })
            .catch((error) => {
              if (error.hasOwnProperty("errors")) {
                this.$notify({
                  title: error.message,
                  message: error.errors[Object.keys(error.errors)[0]],
                  type: "error",
                });
              } else {
                this.$notify({
                  title: error.name,
                  message: error.message,
                  type: "error",
                });
              }
              this.createProcessing = false;
            });
        })
        .catch(() => true);
    },

    editOperation(editBody) {
      this.editProcessing = true;

      this.editDevice([this.selectedDevice.id, editBody])
        .then(() => {
          this.$notify({
            title: "Success",
            message: "Edited successfully!",
            type: "success",
          });
          this.editProcessing = false;
          this.cancelOperation("edit");
        })
        .catch((error) => {
          if (error.hasOwnProperty("errors")) {
            this.$notify({
              title: error.message,
              message: error.errors[Object.keys(error.errors)[0]],
              type: "error",
            });
          } else {
            this.$notify({
              title: error.name,
              message: error.message,
              type: "error",
            });
          }
          this.editProcessing = false;
        });
    },

    deleteOperation(id) {
      this.$confirm("Process device delete?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning",
      })
        .then(() => {
          this.tableLoading = true;
          this.deleteDevice(id)
            .then(() => {
              this.$notify({
                title: "Success",
                message: "Deleted successfully!",
                type: "success",
              });
              this.tableLoading = false;
            })
            .catch((error) => {
              if (error.hasOwnProperty("errors")) {
                this.$notify({
                  title: error.message,
                  message: error.errors[Object.keys(error.errors)[0]],
                  type: "error",
                });
              } else {
                this.$notify({
                  title: error.name,
                  message: error.message,
                  type: "error",
                });
              }
              this.tableLoading = false;
            });
        })
        .catch(() => true);
    },

    bulkRebootOperation(phPmId) {
      feathersClient
        .service("devices")
        .patch(
          null,
          {
            tenantId: this.selectedTenant.id,
            phPmId: phPmId,
          },
          {
            query: {
              tenantId: this.selectedTenant.id,
              editContext: "bulkReboot",
            },
          }
        )
        .then(() => {
          this.$notify({
            type: "success",
            title: "Success",
            message: "Successfully rebooted devices.",
          });
          this.cancelOperation("bulkReboot");
        })
        .catch((error) => {
          if (error.hasOwnProperty("errors")) {
            this.$notify({
              title: error.message,
              message: error.errors[Object.keys(error.errors)[0]],
              type: "error",
            });
          } else {
            this.$notify({
              title: error.name,
              message: error.message,
              type: "error",
            });
          }
          this.cancelOperation("bulkReboot");
        });
    },

    openBulkUpdateModal() {
      this.clearFormFields = false;
      this.bulkUpdateVisible = true;
    },

    openBulkRebootModal() {
      this.clearFormFields = false;
      this.bulkRebootVisible = true;
    },

    rebootDevices(updateBody) {
      return this.$confirm("Reboot all devices?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning",
      }).then(() => {
        updateBody.shouldReboot = true;
      });
    },

    runUpdate(updateBody) {
      return feathersClient
        .service("devices")
        .patch(null, updateBody, {
          query: {
            editContext: "bulkUpdate",
            tenantId: this.selectedTenant.id,
          },
        })
        .then(() => {
          this.$notify({
            type: "success",
            title: "Success",
            message: "Successfully updated devices.",
          });
          this.cancelOperation("bulkUpdate");
        })
        .catch((error) => {
          if (error.hasOwnProperty("errors")) {
            this.$notify({
              title: error.message,
              message: error.errors[Object.keys(error.errors)[0]],
              type: "error",
            });
          } else {
            this.$notify({
              title: error.name,
              message: error.message,
              type: "error",
            });
          }
          this.cancelOperation("bulkUpdate");
        });
    },

    updateDevices(updateBody) {
      this.bulkUpdateProcessing = true;
      // We need to run an update whether we reboot or not, hence the runUpdate in both the then and catch
      return this.rebootDevices(updateBody)
        .then(() => {
          this.runUpdate(updateBody);
        })
        .catch(() => {
          this.runUpdate(updateBody);
        });
    },

    bulkUpdateOperation(updateBody) {
      this.$confirm("Process bulk update device settings?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning",
      })
        .then(() => {
          return this.updateDevices(updateBody);
        })
        .then(() => (this.bulkUpdateProcessing = false))
        .catch(() => true);
    },

    cancelOperation(type) {
      this[`${type}Visible`] = false;

      this.mac = "";
      this.macInUse = null;
      this.clearFormFields = true;
    },

    getdeviceModelOptions() {
      feathersClient
        .service("device-models")
        .find({
          query: {
            $sort: { name: 1 },
          },
        })
        .then((res) => {
          this.deviceModelOptions = [...res];
        });
    },

    openCreateModal() {
      this.clearFormFields = false;
      this.createVisible = true;
    },

    openEditModal(id) {
      this.clearFormFields = false;
      feathersClient
        .service("devices")
        .get(id)
        .then((res) => {
          this.selectedDevice = res;
          this.editVisible = true;
        });
    },

    getPhonebookOptions() {
      feathersClient
        .service("phonebooks")
        .find({
          query: {
            tenantId: this.selectedTenant.id,
            $or: [
              { provisioning: 1 },
              {
                name: "Default",
                includeExt: "yes",
              },
            ],
            $sort: { name: 1 },
          },
        })
        .then((res) => {
          this.phonebooksOptions = [...res];
        }).catch(e => {
          this.phonebooksOptions = [];
      });
    },

    isYealink(mac) {
      if (mac) {
        //only yealink has local config in S3
        const validPrefixes = ["805EC0", "805E0C", "001565", "249AD8", "44DBD2", "C4FC22"];
        const test = mac.replace(/:/g, "").substr(0, 6);
        return validPrefixes.includes(test);
      }
    },

    joinSocketChannel() {
      const channelService = feathersClient.service("channel-memberships");
      const data = {
        roomType: "admins",
        tenantId: this.selectedTenant.id,
      };
      channelService.create(data).then((res) => {
        this.devicesChannel = `${data.roomType}/${data.tenantId}`;
      });
    },

    removeListeners() {
      feathersClient.service("devices").removeListener("patched");
      feathersClient.service("devices").removeListener("created");
      feathersClient.service("devices").removeListener("removed");
      globalEventBus.$off("appReconnected");
    },
  },

  filters: {
    number(value) {
      return !isNaN(value)
        ? new Intl.NumberFormat("en-US").format(value)
        : value;
    },
  },

  created() {
    //event listeners
    //monitor socket connection and reconnect to channel
    globalEventBus.$on("appReconnected", () => {
      this.joinSocketChannel();
    });

    //record was edited
    feathersClient.service("devices").on("patched", (data) => {
      const updateIndex = this.tableData.findIndex(
        (item) => item.id === data.id
      );
      const updateAllIndex = this.allDevices.findIndex(
        (item) => item.id === data.id
      );
      if (updateIndex >= 0) {
        const { userAgent } = this.tableData[updateIndex];
        this.tableData.splice(updateIndex, 1, { ...data, userAgent });
      }
      if (updateAllIndex >= 0) {
        const { userAgent } = this.allDevices[updateAllIndex];
        this.allDevices.splice(updateAllIndex, 1, { ...data, userAgent });
      }
    });

    //record was created
    feathersClient.service("devices").on("created", (data) => {
      this.tableData.push(data);
      this.allDevices.push(data);
    });

    //record was deleted
    feathersClient.service("devices").on("removed", (data) => {
      const indexToRemove = this.tableData.findIndex(
        (item) => item.id === data.id
      );
      const indexAllToRemove = this.allDevices.findIndex(
        (item) => item.id === data.id
      );
      if (indexToRemove >= 0) {
        this.tableData.splice(indexToRemove, 1);
      }
      if (indexAllToRemove >= 0) {
        this.allDevices.splice(indexAllToRemove, 1);
      }
    });
  },

  mounted() {
    if (this.user) {
      this.findDevices();
      this.joinSocketChannel();
      this.getdeviceModelOptions();
      this.getPhonebookOptions();
      this.getButtonLayoutOptions();
    }
  },

  beforeDestroy() {
    this.removeListeners();
    feathersClient
      .service("channel-memberships")
      .remove(null, { query: { channelName: this.devicesChannel } });
  },
};
</script>

<style lang="scss" scoped>
.duplicate-modal {
  &__title {
    margin: 0;
  }
}
</style>
